/**
 * @description Janela "Ajuda e suporte" do teste grátis (29/10): WhatsApp da área comercial
 *              para tirar dúvida em tempo real, 0800 do suporte 24 horas, e-mail, chamados,
 *              Central de Ajuda e o tour guiado. Aberta pelo botão ao lado da Celeri.
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Lucide } from '@/chassis/ui/components';
import { CHANNEL_LABEL, type MyTrial, ymdToBr } from '../service';

/** Área comercial (WhatsApp do Flávio, informado pelo Alexandre em 29/10). */
export const COMERCIAL_WHATSAPP = '5541998308080';
export const COMERCIAL_WHATSAPP_ROTULO = '(41) 99830-8080';
/** Suporte: os mesmos contatos do site (seção Atendimento). */
export const SUPORTE_0800 = '08007416103';
export const SUPORTE_0800_ROTULO = '0800 741 6103';
export const SUPORTE_EMAIL = 'suporte@canaldecomprasbrasil.com.br';

function linkWhatsApp(trial: MyTrial): string {
    const texto = `Olá! Estou no teste grátis do Canal de Compras Brasil${trial.agencyName ? ` (${trial.agencyName.replace(/\s*\(ambiente de teste\)\s*$/i, '')})` : ''} e quero tirar uma dúvida.`;
    return `https://wa.me/${COMERCIAL_WHATSAPP}?text=${encodeURIComponent(texto)}`;
}

export default function AjudaSuporte({ trial, onFechar, onTour }: { trial: MyTrial; onFechar: () => void; onTour: () => void }) {
    useEffect(() => {
        const tecla = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onFechar();
        };
        window.addEventListener('keydown', tecla);
        return () => window.removeEventListener('keydown', tecla);
    }, [onFechar]);

    const contato = trial.contact
        ? `${ymdToBr(trial.contact.date)}, às ${trial.contact.time.replace(':', 'h')}, por ${CHANNEL_LABEL[trial.contact.channel]?.toLowerCase() ?? 'telefone'}`
        : null;

    return createPortal(
        <div className="tg-root tg-ajuda-fundo" onClick={onFechar}>
            <section className="tg-ajuda" role="dialog" aria-modal="true" aria-labelledby="tg-ajuda-titulo" onClick={(e) => e.stopPropagation()} data-testid="trial-help-dialog">
                <div className="tg-ajuda-foto" aria-hidden="true">
                    <img src="/brand/atendimento/atendente-mulher-v3.jpg" alt="" />
                </div>
                <div className="tg-ajuda-corpo">
                    <button type="button" className="tg-fechar" onClick={onFechar} aria-label="Fechar">
                        <Lucide icon="X" className="h-4 w-4" />
                    </button>
                    <p className="tg-ajuda-sobre">Ajuda e suporte</p>
                    <h2 id="tg-ajuda-titulo">Gente que entende de licitação, na hora</h2>
                    <p className="tg-ajuda-intro">Ficou com dúvida no teste? Fale com a nossa equipe agora. Se não conseguir pelo WhatsApp, ligue para o suporte.</p>

                    <a className="tg-canal tg-canal--whats" href={linkWhatsApp(trial)} target="_blank" rel="noreferrer" data-testid="trial-help-whatsapp">
                        <span className="tg-canal-icone"><Lucide icon="MessageCircle" className="h-5 w-5" /></span>
                        <span className="tg-canal-txt">
                            <b>WhatsApp do comercial</b>
                            <span>{COMERCIAL_WHATSAPP_ROTULO} · tire a dúvida em tempo real</span>
                        </span>
                        <Lucide icon="ArrowUpRight" className="tg-canal-seta" />
                    </a>

                    <a className="tg-canal" href={`tel:${SUPORTE_0800}`} data-testid="trial-help-0800">
                        <span className="tg-canal-icone"><Lucide icon="Phone" className="h-5 w-5" /></span>
                        <span className="tg-canal-txt">
                            <b>Suporte {SUPORTE_0800_ROTULO}</b>
                            <span>Ligação gratuita, 24 horas por dia, 7 dias por semana</span>
                        </span>
                        <Lucide icon="ArrowUpRight" className="tg-canal-seta" />
                    </a>

                    <a className="tg-canal" href={`mailto:${SUPORTE_EMAIL}`}>
                        <span className="tg-canal-icone"><Lucide icon="Mail" className="h-5 w-5" /></span>
                        <span className="tg-canal-txt">
                            <b>E-mail do suporte</b>
                            <span>{SUPORTE_EMAIL}</span>
                        </span>
                        <Lucide icon="ArrowUpRight" className="tg-canal-seta" />
                    </a>

                    <div className="tg-ajuda-atalhos">
                        <button type="button" onClick={onTour}>
                            <Lucide icon="Compass" className="h-4 w-4" /> Tour guiado
                        </button>
                        <Link to="/myfaqs" onClick={onFechar}>
                            <Lucide icon="BadgeHelp" className="h-4 w-4" /> Central de Ajuda
                        </Link>
                        <Link to="/mytickets" onClick={onFechar}>
                            <Lucide icon="MessagesSquare" className="h-4 w-4" /> Abrir chamado
                        </Link>
                    </div>

                    {contato ? <p className="tg-ajuda-agenda"><Lucide icon="CalendarClock" className="h-4 w-4" /> Contato agendado: {contato}.</p> : null}
                </div>
            </section>
        </div>,
        document.body,
    );
}
